import React, { useState, useEffect, useRef, memo } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import MUIDataTable from 'mui-datatables'
import api from '../../services/api'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import textLabels from './textLabelsTable'


const getItem = (storage, key) => {
  try {
    const data = localStorage.getItem(storage)
    if (!data) return null
    const parsed = JSON.parse(data)
    return Object.keys(parsed).includes(key) ? parsed[key] : null
  } catch (e) {
    // localStorage corrompido não pode derrubar a listagem.
    return null
  }
}

// Filtros viram uma chave estável: chaves ordenadas e sem valor vazio.
//
// Antes a dependência do efeito era `JSON.stringify(filters)` cru, e
// `JSON.stringify` DESCARTA chaves com valor `undefined`. Como os filtros são
// montados com `|| undefined`, dois estados diferentes geravam a mesma string:
// o efeito não disparava, nenhuma requisição saía, e a tela ficava mostrando o
// resultado anterior enquanto os selects já exibiam o critério novo. Só mexer
// em outro critério "consertava". Normalizar antes resolve os dois lados —
// a chave passa a refletir o filtro efetivo, e o corpo do POST vai limpo.
const normalizeFilters = (filters) => {
  const clean = {}
  Object.keys(filters || {}).sort().forEach((key) => {
    const value = filters[key]
    if (value === undefined || value === null || value === '') return
    clean[key] = value
  })
  return clean
}

export default function ({
  endpoint,
  columns,
  options = {},
  title = "Your title here",
  refresh = false,
  handlerRefresh = () => { },
  empresa = false,
  filters = {},
  onDataLoaded = null
}) {
  const storageKey = "datatable." + endpoint

  const [data, setData] = useState({ data: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [searchText, setSearchText] = useState(() => getItem(storageKey, "searchText") || "")
  const [page, setPage] = useState(() => getItem(storageKey, "page") || 0)
  const [rowsPerPage, setRowsPerPage] = useState(() => getItem(storageKey, "rowsPerPage") || 10)
  const [sortOrder, setSortOrder] = useState(() => getItem(storageKey, "sortOrder") || { name: "id", direction: "asc" })
  const [reloadToken, setReloadToken] = useState(0)

  const cleanFilters = normalizeFilters(filters)
  const filtersKey = JSON.stringify(cleanFilters)

  // Tudo que restringe o RESULTADO (e não a janela dentro dele). Quando isso
  // muda, a página tem que voltar pra primeira: continuar no offset 30 de um
  // filtro que agora devolve 5 linhas mostra tabela vazia sem explicação, e a
  // paginação do mui-datatables não reseta sozinha (o `searchClose` da lib,
  // o "×" que fecha a busca, também não). Ajustar o estado durante o render é
  // o padrão do React pra isso — o efeito de busca já enxerga page = 0 e sai
  // uma requisição só, em vez de uma errada seguida da certa.
  const queryKey = JSON.stringify([endpoint, empresa || null, filtersKey, searchText])
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey)
  if (queryKey !== prevQueryKey) {
    setPrevQueryKey(queryKey)
    if (page !== 0) setPage(0)
  }

  // Descarta resposta de requisição superada. Sem isso, duas buscas em voo
  // (troca de filtro + refresh, por exemplo) terminam com a que chegar por
  // último vencendo — mesmo sendo a mais antiga.
  const requestIdRef = useRef(0)
  const prevSearchRef = useRef(searchText)

  const fetchData = async () => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setLoadError(false)

    const body = {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      query: searchText || "",
      order_by: sortOrder?.name,
      order: sortOrder?.direction,
      ...cleanFilters,
    }
    if (empresa) body.empresa = empresa

    try {
      const response = await api.post(endpoint, body)
      if (requestId !== requestIdRef.current) return
      const loadedData = response?.data || { data: [], total: 0 }
      setData(loadedData)
      if (onDataLoaded) onDataLoaded(loadedData.data)
    } catch (e) {
      if (requestId !== requestIdRef.current) return
      // Falha precisa aparecer. Antes o caminho sem `filters` era um `await`
      // sem try/catch: a promise rejeitava, `setData` nunca rodava e a tabela
      // continuava exibindo as linhas da consulta anterior, como se o filtro
      // não tivesse efeito nenhum.
      setData({ data: [], total: 0 })
      setLoadError(true)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== searchText
    prevSearchRef.current = searchText

    // Só a digitação da busca precisa de debounce. Trocar de página, de
    // filtro ou de ordenação vai direto.
    const delay = searchChanged && searchText ? 500 : 0
    const timeoutId = setTimeout(() => { fetchData() }, delay)

    return () => clearTimeout(timeoutId)
  }, [endpoint, empresa, filtersKey, searchText, page, rowsPerPage,
    sortOrder?.name, sortOrder?.direction, reloadToken])

  // `refresh` continua sendo o gatilho externo das telas, mas agora entra pelo
  // mesmo caminho da busca — antes ele chamava `fetchData()` sem argumentos, o
  // que descartava página, ordenação e texto de busca do usuário.
  useEffect(() => {
    if (!refresh) return
    setReloadToken((token) => token + 1)
    handlerRefresh()
  }, [refresh])

  const bodyMessage = loading
    ? <CircularProgress color="info" />
    : loadError
      ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={1}>
          <Typography variant="body2">Não foi possível carregar os dados.</Typography>
          <Button size="small" variant="outlined"
            onClick={() => setReloadToken((token) => token + 1)}>
            Tentar de novo
          </Button>
        </Box>
      )
      : "Nenhum registro encontrado"

  return (

    <Datatable
      title={title}
      data={data?.data}
      columns={columns}
      options={({
        ...options,
        textLabels: {
          ...textLabels, body: { noMatch: bodyMessage }
        },
        count: data?.total,
        serverSide: true,
        viewColumns: false,
        selectableRows: options.selectableRows || 'none',
        onRowSelectionChange: options.onRowSelectionChange || undefined,
        rowsSelected: options.rowsSelected || undefined,
        rowsPerPageOptions: [10, 30, 50, 100, 200],
        page,
        rowsPerPage,
        storageKey,
        print: false,
        download: false,
        filter: false,
        responsive: 'standard',
        onTableChange: (action, tableState) => {
          switch (action) {
            case 'changePage':
              setPage(tableState.page)
              break;
            case 'changeRowsPerPage':
              setRowsPerPage(tableState.rowsPerPage)
              setPage(tableState.page)
              break;
            case 'search':
              // `searchClose` manda null; quem devolve pra primeira página é a
              // troca de `queryKey`.
              setSearchText(tableState.searchText || "")
              break;
            case 'sort':
              if (tableState.sortOrder?.name) {
                setSortOrder({
                  name: tableState.sortOrder.name,
                  direction: tableState.sortOrder.direction
                })
              }
              break;
            default:
            //console.log(action, tableState);
          }
        },

      })}
    />

  )

}



export const Datatable = memo(function ({ data, columns, options = {}, title = "Your title here" }) {
  const defaultTheme = createTheme(
    {

      pallete: {

        mode: 'light',
        primary: {
          main: "#e01b29",
        },

        background: {
          paper: '#EDF1FC',
        },
        text: {
          primary: "#2B2E35",

        },
      }
      ,
      typography: {
        fontFamily: [
          'sans-serif',
          'Roboto',
          'Montserrat',
        ]
      },


    }
  )

  return (

    <ThemeProvider theme={defaultTheme}>

      <MUIDataTable
        title={title}
        data={data}
        columns={columns}
        options={options}
      />
    </ThemeProvider>

  )

})
